function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function makeFixedShade(gridSize, alpha) {
  const shade = new Uint8ClampedArray(gridSize * gridSize)
  shade.fill(alpha)
  return shade
}

export function computeShadeFromElevationGrid({
  elevations,
  gridSize,
  sunAltitude,
  sunAzimuth,
  dx,
  dy,
  forceShade = false
}) {
  if (forceShade) return makeFixedShade(gridSize, 120)
  if (sunAltitude <= 0) return makeFixedShade(gridSize, 200)

  const shade = new Uint8ClampedArray(gridSize * gridSize)

  const azimuthRad = (sunAzimuth * Math.PI) / 180
  const sunAltRad = (sunAltitude * Math.PI) / 180
  const dirX = Math.sin(azimuthRad)
  const dirY = -Math.cos(azimuthRad)
  const stepMeters = Math.hypot((dx || 0) * dirX, (dy || 0) * dirY) || 1

  const sunFactor = clamp((sunAltitude - 8) / 75, 0, 1)
  const shadowAlpha = Math.round(62 + (1 - sunFactor) * 112)
  const hillshadeMax = Math.round(34 + (1 - sunFactor) * 62)
  const ambient = 0.34

  const lightX = Math.sin(azimuthRad) * Math.cos(sunAltRad)
  const lightY = -Math.cos(azimuthRad) * Math.cos(sunAltRad)
  const lightZ = Math.sin(sunAltRad)

  const maxElevDiff = 5200
  const safeAlt = Math.max(1 * (Math.PI / 180), sunAltRad)
  const maxShadowDistance = maxElevDiff / Math.tan(safeAlt)
  const maxSteps = Math.min(gridSize, Math.max(1, Math.ceil(maxShadowDistance / stepMeters)))
  const shadowBiasRad = (
    (sunAltitude > 45 ? 1.4 : sunAltitude > 25 ? 1.0 : 0.55) * Math.PI
  ) / 180

  for (let row = 0; row < gridSize; row += 1) {
    for (let col = 0; col < gridSize; col += 1) {
      const idx = row * gridSize + col
      const baseElevation = elevations[idx]
      if (!Number.isFinite(baseElevation)) {
        shade[idx] = 0
        continue
      }

      const isWater = baseElevation <= 1
      let hillAlpha = 0

      if (!isWater) {
        const west = col > 0 ? elevations[idx - 1] : baseElevation
        const east = col < gridSize - 1 ? elevations[idx + 1] : baseElevation
        const north = row > 0 ? elevations[idx - gridSize] : baseElevation
        const south = row < gridSize - 1 ? elevations[idx + gridSize] : baseElevation

        const zW = Number.isFinite(west) ? west : baseElevation
        const zE = Number.isFinite(east) ? east : baseElevation
        const zN = Number.isFinite(north) ? north : baseElevation
        const zS = Number.isFinite(south) ? south : baseElevation

        const safeDx = Number.isFinite(dx) && dx > 0 ? dx : 1
        const safeDy = Number.isFinite(dy) && dy > 0 ? dy : 1
        const dzdx = (zE - zW) / (2 * safeDx)
        const dzdy = (zS - zN) / (2 * safeDy)
        const slope = Math.hypot(dzdx, dzdy)
        const steepFactor = clamp((slope - 0.045) / 0.34, 0, 1)

        let nx = -dzdx
        let ny = -dzdy
        let nz = 1
        const norm = Math.hypot(nx, ny, nz) || 1
        nx /= norm
        ny /= norm
        nz /= norm

        const intensity = Math.max(0, nx * lightX + ny * lightY + nz * lightZ)
        const lit = ambient + (1 - ambient) * intensity
        const darkness = Math.pow(clamp(1 - lit, 0, 1), 1.6)
        hillAlpha = Math.round(darkness * hillshadeMax * steepFactor)
      }

      let inShadow = false
      if (!isWater) {
        for (let step = 1; step <= maxSteps; step += 1) {
          const rr = Math.round(row + dirY * step)
          const cc = Math.round(col + dirX * step)
          if (rr < 0 || rr >= gridSize || cc < 0 || cc >= gridSize) break
          const nextElevation = elevations[rr * gridSize + cc]
          if (!Number.isFinite(nextElevation)) continue
          const distance = step * stepMeters
          const angle = Math.atan2(nextElevation - baseElevation, distance)
          if (angle > sunAltRad - shadowBiasRad) {
            inShadow = true
            break
          }
        }
      }

      const combined = inShadow
        ? shadowAlpha + hillAlpha * 0.35
        : hillAlpha
      shade[idx] = clamp(Math.round(combined), 0, 205)
    }
  }

  return shade
}
